import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { X, Bug, Image as ImageIcon, Trash2, Paperclip, Camera, Pencil } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from './ModalStyles';
import supportTaskService, {
  SupportTaskPriority,
  getTaskPriorityLabel,
} from '../api/services/supportTaskService';
import * as storageService from '../api/services/storageService';
import { getBugReportContext } from '../utils/bugReporterContext';
import { SupportTaskPriorityColors } from '../constants/statusColors';
import { ImageAnnotator } from './ImageAnnotator';

const Title = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  color: ${props => props.theme.colors.text};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.muted};
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 6px;
  &:hover { background: ${props => props.theme.colors.backgroundSecondary}; }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: 6px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.muted};
  margin-top: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

const PriorityGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const PriorityChip = styled.button`
  padding: 5px 12px;
  border: 1.5px solid ${props => props.$color};
  border-radius: 20px;
  background: ${props => props.$selected ? props.$color : 'transparent'};
  color: ${props => props.$selected
    ? (props.$priority === 'Medium' ? '#000' : '#fff')
    : props.$color};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  line-height: 1.4;

  &:hover:not(:disabled) {
    background: ${props => props.$selected ? props.$color : props.$color + '20'};
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  &:focus { outline: none; border-color: ${props => props.theme.colors.tint}; }
`;

const AttachRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const AttachButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  color: ${props => props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  &:hover { background: ${props => props.theme.colors.backgroundSecondary}; border-color: ${props => props.theme.colors.tint}; }
`;

const ThumbStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const Thumb = styled.div`
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  overflow: hidden;
  background: ${props => props.theme.colors.backgroundSecondary};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 100%;
    display: block;
  }
`;

const ThumbRemove = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(0, 0, 0, 0.75); }
`;

const ThumbEdit = styled.button`
  position: absolute;
  top: 4px;
  left: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(239, 68, 68, 0.85); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const ThumbBadge = styled.div`
  position: absolute;
  bottom: 4px;
  left: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
`;

const CaptureNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${props => props.theme.colors.muted};
  margin-top: 8px;
  font-style: italic;
`;

const MetaPreview = styled.details`
  margin-top: 4px;
  font-size: 12px;
  color: ${props => props.theme.colors.muted};
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 8px 10px;

  summary {
    cursor: pointer;
    font-weight: 600;
    color: ${props => props.theme.colors.text};
    user-select: none;
  }

  pre {
    margin: 8px 0 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 11px;
    max-height: 240px;
    overflow: auto;
  }
`;

const BtnPrimary = styled.button`
  padding: 9px 16px;
  background: ${props => props.theme.colors.tint};
  color: #fff;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(1.05); }
`;

const BtnSecondary = styled.button`
  padding: 9px 16px;
  background: transparent;
  color: ${props => props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { background: ${props => props.theme.colors.backgroundSecondary}; }
`;

const PRIORITY_OPTIONS = [
  SupportTaskPriority.LOW,
  SupportTaskPriority.MEDIUM,
  SupportTaskPriority.HIGH,
  SupportTaskPriority.CRITICAL,
];

/**
 * QA Bug Report overlay. Triggered from the top-bar Bug icon.
 *
 * Submission flow:
 *  1. User types title + (optional) description, picks priority, attaches images
 *     (paste or file picker — both supported).
 *  2. Each image uploads to S3 immediately via storageService.uploadFileToBlob;
 *     we hold blobPath in state until the task is created.
 *  3. On submit:
 *     a. createTask({title, description: user-text-only, taskType: Bug, priority})
 *     b. for each uploaded image, supportTaskService.addAttachment(...)
 *     c. build a bug-context.json from getBugReportContext(), upload it,
 *        attach it as a separate file (so triagers see the metadata as a
 *        structured JSON attachment, not noise in the description).
 */
export const BugReportModal = ({ isOpen, onClose }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [title, setTitle] = useState('[Bug] ');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(SupportTaskPriority.MEDIUM);
  const [images, setImages] = useState([]); // {id, fileName, contentType, fileSize, file, storagePath, previewUrl, uploading, error}
  const [submitting, setSubmitting] = useState(false);
  const [capturingPage, setCapturingPage] = useState(false);
  const [editingImageId, setEditingImageId] = useState(null);
  const titleRef = useRef(null);
  const fileInputRef = useRef(null);

  const editingImage = editingImageId
    ? images.find(i => i.id === editingImageId)
    : null;

  // Reset on open + autofocus title.
  useEffect(() => {
    if (isOpen) {
      setTitle('[Bug] ');
      setDescription('');
      setPriority(SupportTaskPriority.MEDIUM);
      setImages([]);
      setSubmitting(false);
      setTimeout(() => titleRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Dirty if user has typed anything beyond the placeholder, changed priority,
  // or attached anything. Used to gate the discard-confirm prompt.
  const isDirty =
    title.trim() !== '[Bug]' ||
    description.trim() !== '' ||
    priority !== SupportTaskPriority.MEDIUM ||
    images.length > 0;

  const requestClose = () => {
    if (submitting) return;
    if (isDirty) {
      const ok = window.confirm(
        'Discard this bug report? Your description and attachments will be lost.',
      );
      if (!ok) return;
    }
    images.forEach(p => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
    onClose();
  };

  // Esc closes (with discard prompt). Backdrop click is intentionally ignored.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        requestClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isDirty, submitting]);

  // Fresh JSON snapshot for the preview (recomputes on open + when images change
  // so newly-captured console errors show up).
  const ctxJson = useMemo(
    () => (isOpen ? getBugReportContext({ user }) : null),
    [isOpen, user, images.length],
  );

  const ingestImageFile = useCallback(async (file, opts = {}) => {
    if (!file || !file.type?.startsWith('image/')) return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const fileName = file.name && file.name !== 'image.png'
      ? file.name
      : `screenshot-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
    const previewUrl = URL.createObjectURL(file);

    setImages(prev => [
      ...prev,
      {
        id,
        fileName,
        contentType: file.type || 'image/png',
        fileSize: file.size,
        file,
        previewUrl,
        uploading: true,
        storagePath: null,
        error: null,
        auto: !!opts.auto,
      },
    ]);

    try {
      const { blobPath } = await storageService.uploadFileToBlob(file);
      setImages(prev =>
        prev.map(img =>
          img.id === id ? { ...img, uploading: false, storagePath: blobPath } : img,
        ),
      );
    } catch (err) {
      console.error('Bug-report: image upload failed', err);
      setImages(prev =>
        prev.map(img =>
          img.id === id ? { ...img, uploading: false, error: err.message } : img,
        ),
      );
      if (!opts.auto) toast.error(`Failed to upload ${fileName}`);
    }
  }, []);

  // Auto-capture the current page once when the modal opens. The ModalOverlay is
  // tagged with data-bug-reporter-ignore so html2canvas skips the entire modal
  // tree and only captures the page underneath.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setCapturingPage(true);

    // Defer two frames so styled-components has flushed and the data-attr is on the DOM.
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(async () => {
        try {
          const canvas = await html2canvas(document.body, {
            ignoreElements: (el) => el?.dataset?.bugReporterIgnore === 'true',
            useCORS: true,
            logging: false,
            backgroundColor: null,
            // Visible viewport only — don't try to rasterize the full scroll height
            width: window.innerWidth,
            height: window.innerHeight,
            x: window.scrollX,
            y: window.scrollY,
            scrollX: 0,
            scrollY: -window.scrollY,
          });
          if (cancelled) return;
          const blob = await new Promise(res => canvas.toBlob(res, 'image/png', 0.92));
          if (!blob || cancelled) return;
          const stamp = new Date().toISOString().replace(/[:.]/g, '-');
          const file = new File([blob], `page-${stamp}.png`, { type: 'image/png' });
          await ingestImageFile(file, { auto: true });
        } catch (err) {
          console.warn('Bug-report: auto page capture failed', err);
        } finally {
          if (!cancelled) setCapturingPage(false);
        }
      });
      // Cleanup inner frame
      return () => cancelAnimationFrame(raf2);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItems = Array.from(items).filter(i => i.type.startsWith('image/'));
    if (imageItems.length === 0) return;
    e.preventDefault();
    for (const item of imageItems) {
      const file = item.getAsFile();
      if (file) await ingestImageFile(file);
    }
  };

  const handleFilePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same file
    for (const f of files) await ingestImageFile(f);
  };

  const removeImage = (id) => {
    setImages(prev => {
      const target = prev.find(p => p.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(p => p.id !== id);
    });
  };

  // Save annotated image: re-upload the merged PNG and replace the entry's
  // storagePath + previewUrl. Old preview URL is revoked so memory doesn't leak.
  const handleAnnotationSave = async (blob) => {
    const id = editingImageId;
    if (!id || !blob) return;
    const target = images.find(i => i.id === id);
    if (!target) return;

    const newName = target.fileName.replace(/(\.[a-z]+)?$/i, '-annotated.png');
    const newFile = new File([blob], newName, { type: 'image/png' });
    const newPreview = URL.createObjectURL(blob);
    const oldPreview = target.previewUrl;

    setImages(prev => prev.map(img => img.id === id
      ? { ...img, fileName: newName, contentType: 'image/png', fileSize: newFile.size,
          file: newFile, previewUrl: newPreview, uploading: true, storagePath: null, error: null }
      : img));
    if (oldPreview) URL.revokeObjectURL(oldPreview);
    setEditingImageId(null);

    try {
      const { blobPath } = await storageService.uploadFileToBlob(newFile);
      setImages(prev => prev.map(img => img.id === id
        ? { ...img, uploading: false, storagePath: blobPath }
        : img));
    } catch (err) {
      console.error('Bug-report: annotated image upload failed', err);
      setImages(prev => prev.map(img => img.id === id
        ? { ...img, uploading: false, error: err.message }
        : img));
      toast.error(`Failed to upload annotated ${newName}`);
    }
  };

  const anyUploading = images.some(i => i.uploading);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (anyUploading) {
      toast.info('Wait for images to finish uploading');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        taskType: 'Bug',
        priority,
        projectScope: 'Portal',
        assignedToUserId: null,
        dueDate: null,
        sentryIssueId: null,
        sentryIssueUrl: null,
      };

      const result = await supportTaskService.createTask(payload);
      const task = result.data || result;

      // Attach images that uploaded successfully.
      const ready = images.filter(i => i.storagePath && !i.error);
      for (const img of ready) {
        try {
          await supportTaskService.addAttachment(task.id, {
            fileName: img.fileName,
            storagePath: img.storagePath,
            contentType: img.contentType,
            fileSize: img.fileSize,
          });
        } catch (attachErr) {
          console.error('Bug-report: failed to register image attachment', img.fileName, attachErr);
          toast.warning(`Task created, but attachment "${img.fileName}" failed to register`);
        }
      }

      // Upload the captured context as a JSON attachment.
      try {
        const ctx = getBugReportContext({ user });
        const json = JSON.stringify(ctx, null, 2);
        const stamp = new Date().toISOString().replace(/[:.]/g, '-');
        const jsonFile = new File([json], `bug-context-${stamp}.json`, {
          type: 'application/json',
        });
        const { blobPath } = await storageService.uploadFileToBlob(jsonFile);
        await supportTaskService.addAttachment(task.id, {
          fileName: jsonFile.name,
          storagePath: blobPath,
          contentType: 'application/json',
          fileSize: jsonFile.size,
        });
      } catch (jsonErr) {
        console.error('Bug-report: failed to attach context JSON', jsonErr);
        toast.warning('Task created, but the metadata JSON failed to attach');
      }

      toast.success(`Bug reported as task #${task.id}`);

      // Cleanup object URLs (already-clean state on success — no discard prompt).
      images.forEach(p => p.previewUrl && URL.revokeObjectURL(p.previewUrl));

      onClose();
      navigate(`/tasks/${task.id}`);
    } catch (err) {
      console.error('Bug-report: submit failed', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to submit bug report');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay theme={theme} $isOpen={isOpen} data-bug-reporter-ignore="true">
      <ModalContent theme={theme} onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <ModalHeader theme={theme}>
          <Title theme={theme}>
            <Bug size={20} color={theme.colors.tint} />
            Report a Bug (QA)
          </Title>
          <CloseButton theme={theme} onClick={requestClose} aria-label="Close">
            <X size={18} />
          </CloseButton>
        </ModalHeader>

        <ModalBody theme={theme}>
          <div>
            <Label theme={theme}>Title</Label>
            <Input
              ref={titleRef}
              theme={theme}
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Short summary of the bug"
              maxLength={200}
            />
          </div>

          <div>
            <Label theme={theme}>Priority</Label>
            <PriorityGroup>
              {PRIORITY_OPTIONS.map(p => (
                <PriorityChip
                  key={p}
                  type="button"
                  $color={SupportTaskPriorityColors[p] || theme.colors.border}
                  $selected={priority === p}
                  $priority={p}
                  onClick={() => setPriority(p)}
                  disabled={submitting}
                >
                  {getTaskPriorityLabel(p)}
                </PriorityChip>
              ))}
            </PriorityGroup>
          </div>

          <div>
            <Label theme={theme}>What happened?</Label>
            <Textarea
              theme={theme}
              value={description}
              onChange={e => setDescription(e.target.value)}
              onPaste={handlePaste}
              placeholder="Steps to reproduce, what you expected, what happened. Paste screenshots directly here (Cmd/Ctrl+V) or use Attach Image below."
              rows={6}
            />
            <Hint theme={theme}>
              <ImageIcon size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
              Tip: paste screenshots directly into the box above, or attach files below.
            </Hint>

            <AttachRow>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleFilePicked}
              />
              <AttachButton
                type="button"
                theme={theme}
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
              >
                <Paperclip size={14} />
                Attach Image
              </AttachButton>
              {images.length > 0 && (
                <span style={{ fontSize: 12, color: theme.colors.muted }}>
                  {images.length} attached
                </span>
              )}
            </AttachRow>

            {capturingPage && (
              <CaptureNotice theme={theme}>
                <Camera size={12} />
                Capturing page screenshot…
              </CaptureNotice>
            )}

            {images.length > 0 && (
              <ThumbStrip>
                {images.map(img => (
                  <Thumb key={img.id} theme={theme} title={img.fileName}>
                    <img src={img.previewUrl} alt={img.fileName} />
                    {img.uploading && <ThumbBadge>uploading…</ThumbBadge>}
                    {img.error && <ThumbBadge style={{ background: '#dc3545' }}>failed</ThumbBadge>}
                    {img.auto && !img.uploading && !img.error && (
                      <ThumbBadge style={{ background: 'rgba(89, 202, 238, 0.85)' }}>auto</ThumbBadge>
                    )}
                    <ThumbEdit
                      type="button"
                      onClick={() => setEditingImageId(img.id)}
                      disabled={img.uploading}
                      aria-label={`Annotate ${img.fileName}`}
                      title="Annotate with red pen"
                    >
                      <Pencil size={12} />
                    </ThumbEdit>
                    <ThumbRemove
                      type="button"
                      onClick={() => removeImage(img.id)}
                      aria-label={`Remove ${img.fileName}`}
                    >
                      <Trash2 size={12} />
                    </ThumbRemove>
                  </Thumb>
                ))}
              </ThumbStrip>
            )}
          </div>

          <MetaPreview theme={theme}>
            <summary>
              Auto-attached <code>bug-context.json</code> (preview)
            </summary>
            <pre>{ctxJson ? JSON.stringify(ctxJson, null, 2) : ''}</pre>
          </MetaPreview>
        </ModalBody>

        <ModalFooter theme={theme}>
          <BtnSecondary theme={theme} onClick={requestClose} disabled={submitting}>
            Cancel
          </BtnSecondary>
          <BtnPrimary
            theme={theme}
            onClick={handleSubmit}
            disabled={submitting || anyUploading || !title.trim()}
          >
            {submitting ? 'Submitting…' : 'Submit Bug'}
          </BtnPrimary>
        </ModalFooter>
      </ModalContent>

      {editingImage && (
        <ImageAnnotator
          isOpen
          imageUrl={editingImage.previewUrl}
          fileName={editingImage.fileName}
          onSave={handleAnnotationSave}
          onCancel={() => setEditingImageId(null)}
        />
      )}
    </ModalOverlay>
  );
};

export default BugReportModal;
